/**
 * CookieConsentBanner — GDPR baseline.
 *
 * Single bottom-of-screen banner that appears once per browser. Stores
 * the user's choice in localStorage so it doesn't re-appear on every
 * page load. Two options: Accept (analytics + functional cookies) or
 * Decline (functional cookies only — strictly necessary).
 *
 * Doesn't actually wire to any analytics yet (Prime Pillar hasn't added
 * GA/Mixpanel/etc.). When you DO add analytics, gate the initialization
 * on `localStorage.pp_cookie_consent === "accepted"`.
 *
 * Anchored at z-index 40 so it sits ABOVE the PWA install banner (z-30)
 * but BELOW modals (z-50). Slides up on mount with framer-style transition.
 */
import React, { useEffect, useState } from "react";

const CONSENT_KEY = "pp_cookie_consent";

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const choice = localStorage.getItem(CONSENT_KEY);
    if (!choice) {
      // Brief delay so the banner doesn't fight with first-paint
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  const choose = (value) => {
    localStorage.setItem(CONSENT_KEY, value);
    localStorage.setItem(`${CONSENT_KEY}_at`, new Date().toISOString());
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-3 left-3 right-3 sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-md z-40 transition-all"
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
    >
      <div className="rounded-2xl bg-zinc-900 text-white shadow-2xl ring-1 ring-zinc-700 p-4 sm:p-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-400 mb-1.5">
          Cookies + Privacy
        </div>
        <p className="text-[13px] leading-snug text-zinc-200">
          We use functional cookies to keep you signed in and remember your filter
          preferences. Optionally, we&apos;d like to set analytics cookies to understand
          which opportunities resonate so we can ship better signals.
        </p>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <button
            onClick={() => choose("accepted")}
            className="px-3.5 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-zinc-900 text-[12px] font-bold transition-colors"
            data-testid="cookie-accept-btn"
          >
            Accept all
          </button>
          <button
            onClick={() => choose("declined")}
            className="px-3.5 py-1.5 rounded-full bg-zinc-700 hover:bg-zinc-600 text-white text-[12px] font-medium transition-colors"
            data-testid="cookie-decline-btn"
          >
            Functional only
          </button>
          <a
            href="/privacy"
            className="ml-auto text-[11px] text-zinc-400 hover:text-zinc-200 underline decoration-dotted"
            data-testid="cookie-privacy-link"
          >
            Privacy policy
          </a>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
